import React from "react";
import {
  FieldValues,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";
import { Input } from "@/components/ui/input";
import { NonEditableFormField } from "../pages/reservations/guests/components/form/FormFields";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

const FIELD_STYLES = {
  container: "space-y-2 w-full",
} as const;

interface TimePickerProps<T extends FieldValues> {
  id: string;
  label: string;
  required?: boolean;
  register?: UseFormRegister<T>;
  error?: string;
  setValue?: UseFormSetValue<T>;
  watch?: UseFormWatch<T>;
  isEdit?: boolean;
  isCopy?: boolean;
  className?: string;
  disabled?: boolean;
  value?: string | number;
  max?: string;
  min?: string;
  placeholder?: string;
  onChange?: (value: string) => void;
}

export const TimePicker = <T extends FieldValues>({
  id,
  label,
  required = false,
  register,
  error,
  setValue,
  watch,
  isEdit = true,
  isCopy = false,
  className,
  disabled = false,
  value,
  max,
  min,
  placeholder = "Select time",
  onChange,
}: TimePickerProps<T>) => {
  const timeValue = watch ? (watch as any)(id) : value;

  if (!isEdit) {
    return (
      <NonEditableFormField
        isCopy={isCopy}
        value={timeValue || placeholder}
        label={label}
      />
    );
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    if (setValue) {
      (setValue as any)(id, newValue as any);
    }
    if (onChange) onChange(newValue);
  };

  return (
    <div className={FIELD_STYLES.container}>
      <Label htmlFor={id}>
        {label} {required && "*"}
      </Label>
      <Input
        id={id}
        type="time"
        placeholder={placeholder}
        {...(register ? (register as any)(id) : {})}
        value={timeValue as any}
        onChange={handleChange}
        aria-invalid={!!error}
        className={cn(className)}
        disabled={disabled}
        min={min}
        max={max}
      />
      {error && <p className="text-destructive text-sm">{error}</p>}
    </div>
  );
};
